import { useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { MOBILE_SEARCH_ASSISTANTS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { useMobileSearchAssistants } from '@mobileHooks'
import { Box, Button, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileSearchAssistants = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllSearchAssistants = matchPath(ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS, pathname)

  const { searchAssistants, loading, copySearchAssistant, deleteSearchAssistant } =
    useMobileSearchAssistants()

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [searchAssistantToCopy, setSearchAssistantToCopy] = useState<{
    id: string
    name: string
  } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [searchAssistantToDelete, setSearchAssistantToDelete] = useState<{
    id: string
    name: string
  } | null>(null)

  const handleCreate = () => {
    navigate(ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_CREATE)
  }

  const handleEdit = (id: string) => {
    navigate(`${ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_EDIT}/${id}`)
  }

  const handleCopyClick = (id: string) => {
    const searchAssistant = searchAssistants.find(sa => sa.id === id)
    if (searchAssistant) {
      setSearchAssistantToCopy({ id: searchAssistant.id, name: searchAssistant.name })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = (newName: string) => {
    if (searchAssistantToCopy) {
      const newSearchAssistant = copySearchAssistant(searchAssistantToCopy.id, newName)
      if (newSearchAssistant) {
        setCopyDialogOpen(false)
        setSearchAssistantToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_EDIT}/${newSearchAssistant.id}`)
      }
    }
  }

  const handleDeleteClick = (id: string) => {
    const searchAssistant = searchAssistants.find(sa => sa.id === id)
    if (searchAssistant) {
      setSearchAssistantToDelete({ id: searchAssistant.id, name: searchAssistant.name })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = () => {
    if (searchAssistantToDelete) {
      deleteSearchAssistant(searchAssistantToDelete.id)
      setDeleteDialogOpen(false)
      setSearchAssistantToDelete(null)
    }
  }

  const handleExport = (id: string) => {
    const searchAssistant = searchAssistants.find(sa => sa.id === id)
    if (searchAssistant) {
      exportToJSON(searchAssistant, `${searchAssistant.name}_search_assistant_config.json`)
    }
  }

  const columns = [
    ...MOBILE_SEARCH_ASSISTANTS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: params => handleEdit(params.row.id),
      onCopy: params => handleCopyClick(params.row.id),
      onDelete: params => handleDeleteClick(params.row.id),
      onDowloadButtonClick: params => handleExport(params.row.id),
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy Mobile Search Assistant'
        initialName={searchAssistantToCopy?.name || ''}
        existingNames={searchAssistants.map(sa => sa.name)}
        onClose={() => {
          setCopyDialogOpen(false)
          setSearchAssistantToCopy(null)
        }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete Mobile Search Assistant'
        message='Are you sure you want to delete search assistant'
        itemName={searchAssistantToDelete?.name}
        onClose={() => {
          setDeleteDialogOpen(false)
          setSearchAssistantToDelete(null)
        }}
        onConfirm={handleDelete}
      />

      <Box sx={{ ...(!isAllSearchAssistants && { display: 'none' }), flex: 1 }}>
        <PageContentLayout sx={{ p: 1 }}>
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                Mobile Search Assistants
              </Typography>
            }
          />

          <Box sx={{ mb: 2 }}>
            <Button variant='contained' onClick={handleCreate}>
              Create Mobile Search Assistant
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid
              rows={searchAssistants}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
            />
          </Box>
        </PageContentLayout>
      </Box>
    </>
  )
}





